/**
 * Amplitude custom ecommerce instrumentation.
 *
 * Autocapture only produces "[Amplitude] *" events. Every event in the Cupra tracking
 * plan (Product Viewed, Product Added, Checkout Started, ...) needs an explicit
 * track() call — this file makes those calls.
 *
 * Event names and property names below are the ingested names from the Amplitude
 * tracking plan for project 818331. They are lowercase, space-separated, and must
 * match exactly or the data lands as a new off-plan property.
 *
 * Checkout-side events (Order Completed, Shipping Information Added, Payment Method
 * Selected) cannot be tracked here — Shopify checkout does not run theme code. Those
 * live in amplitude-web-pixel.js, installed via Settings > Customer events.
 */
(function () {
  'use strict';

  if (!window.KoppaConsent || !window.KoppaConsent.analyticsAllowed()) {
    window.addEventListener('koppa:consent', function startWhenAllowed() {
      if (!window.KoppaConsent || !window.KoppaConsent.analyticsAllowed()) return;
      window.removeEventListener('koppa:consent', startWhenAllowed);
      startTracking();
    });
    return;
  }
  startTracking();

  function startTracking() {
  var ctx = window.CupraAnalytics || {};
  var queue = [];
  var ready = false;

  // ── Transport ──

  function amp() {
    // Keep the theme's existing tracking project separate from the installed
    // Shopify app, which owns window.amplitude and initializes independently.
    var a = window.koppaAmplitude;
    if (!a) return null;
    if (typeof a.track === 'function') return a;
    // The unified bundle namespaces analytics separately in some builds.
    if (a.analytics && typeof a.analytics.track === 'function') return a.analytics;
    return null;
  }

  function permitted() {
    return !!(window.KoppaConsent && window.KoppaConsent.analyticsAllowed());
  }

  window.addEventListener('koppa:consent', function () {
    if (!permitted()) {
      queue.length = 0; ready = false;
      try { sessionStorage.removeItem('cupra_pending_form'); } catch (_) {}
    }
  });

  function flush() {
    if (!permitted()) { queue.length = 0; return; }
    var instance = amp();
    if (!instance) return;
    ready = true;
    while (queue.length && permitted()) {
      var q = queue.shift();
      try {
        instance.track(q.name, q.props, { time: q.time });
      } catch (e) {
        console.warn('[amplitude] track failed:', q.name, e);
      }
    }
  }

  /**
   * Queue-then-drain: the SDK arrives as an async ES module import, so early events
   * (Product Viewed on a fast page) would otherwise be dropped. Queued events keep
   * their original timestamp.
   */
  function track(name, props) {
    if (!permitted()) return;
    var clean = {};
    Object.keys(props || {}).forEach(function (k) {
      var v = props[k];
      if (v !== null && v !== undefined && v !== '') clean[k] = v;
    });

    queue.push({ name: name, props: clean, time: Date.now() });
    if (ready || amp()) flush();
  }

  window.addEventListener('amplitude:ready', flush);

  // Fallback for load-order surprises (module executed before this file, CDN latency).
  var waited = 0;
  var poll = setInterval(function () {
    waited += 250;
    if (amp()) {
      flush();
      clearInterval(poll);
    } else if (waited >= 10000) {
      clearInterval(poll);
      // Internal visits (team, agents) never load the SDK on purpose (koppa-amplitude-identity.js).
      if (window.KoppaAnalyticsIdentity && window.KoppaAnalyticsIdentity.excluded) { queue.length = 0; return; }
      console.warn('[amplitude] SDK never became available; ' + queue.length + ' events dropped');
    }
  }, 250);

  // ── Helpers ──

  var money = function (cents) {
    return typeof cents === 'number' ? Math.round(cents) / 100 : null;
  };

  function slug(text) {
    return String(text || '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 80);
  }

  function text(el) {
    return el ? String(el.textContent || '').replace(/\s+/g, ' ').trim() : '';
  }

  function base() {
    return Object.assign({ currency: ctx.currency, region: ctx.region, language:ctx.language,
      page_path:ctx.pagePath || location.pathname, page_type:ctx.pageType }, window.KoppaDetailedTracking ? window.KoppaDetailedTracking.context() : {});
  }

  /** Shape matching the plan's `product_engagement.*` property group. */
  function engagement(item) {
    return {
      product_id: item.product_id,
      product_name: item.product_name,
      variant_id: item.variant_id,
      quantity: item.quantity,
      price: item.price
    };
  }

  /** Shape matching the plan's `cart_contents.*` property group. */
  function contents(items) {
    return (items || []).map(function (i) {
      return {
        product_id: i.product_id,
        variant_id: i.variant_id,
        quantity: i.quantity,
        price: i.price,
        selling_plan_id: i.selling_plan_id,
        selling_plan_name: i.selling_plan_name,
        purchase_type: i.purchase_type
      };
    });
  }

  function referrerType() {
    var ref = document.referrer;
    if (!ref) return 'direct';
    try {
      var host = new URL(ref).hostname;
      if (host === window.location.hostname) return 'internal';
      if (/google|bing|duckduckgo|yahoo/.test(host)) return 'search';
      if (/instagram|facebook|tiktok|pinterest|twitter|x\.com|reddit|youtube/.test(host)) return 'social';
      return 'referral';
    } catch (e) {
      return 'unknown';
    }
  }

  // ── Product Viewed ──

  if (ctx.pageType === 'product' && ctx.product) {
    var p = ctx.product;
    track('Product Viewed', Object.assign(base(), {
      'product id': p.product_id,
      'product name': p.product_name,
      'variant id': p.variant_id,
      'variant name': p.variant_name,
      sku: p.sku,
      'product handle': p.handle,
      in_stock: p.available,
      price: p.price,
      'collection name': p.collection_name,
      'referrer type': referrerType(),
      product_engagement: engagement({
        product_id: p.product_id,
        product_name: p.product_name,
        variant_id: p.variant_id,
        quantity: 1,
        price: p.price
      })
    }));
  }

  // ── Product Variant Selected ──

  var lastVariantId = ctx.product ? ctx.product.variant_id : null;
  var lastVariantTrackedAt = 0;

  /**
   * Dawn's pubsub and the raw `change` listener below both observe the same click on a
   * Dawn product page. Suppress the second one so the option change counts once.
   */
  function variantRecentlyTracked() {
    return Date.now() - lastVariantTrackedAt < 1000;
  }

  function variantSelected(variant, optionType, optionValue) {
    if (!variant || variant.id === lastVariantId) return;
    lastVariantId = variant.id;
    lastVariantTrackedAt = Date.now();

    track('Product Variant Selected', Object.assign(base(), {
      'product id': ctx.product ? ctx.product.product_id : null,
      'product name': ctx.product ? ctx.product.product_name : null,
      'variant id': variant.id,
      'variant name': variant.title,
      price: money(variant.price),
      'option type': optionType,
      'option value': optionValue
    }));
  }

  // Dawn publishes variant changes through the theme's pubsub module.
  if (typeof window.subscribe === 'function') {
    try {
      window.subscribe('variant-change', function (event) {
        var variant = event && (event.data ? event.data.variant : event.variant);
        if (!variant) return;
        var optionType = (variant.options && variant.options.length === 1) ? 'variant' : 'variant combination';
        variantSelected(variant, optionType, variant.title);
      });
    } catch (e) {
      /* pubsub unavailable on this template */
    }
  }

  // Direct listener covers the custom Cupra sections, which don't use pubsub.
  document.addEventListener('change', function (e) {
    var input = e.target;
    if (!input || !input.name) return;

    var picker = input.closest('variant-selects, .product-form__input, [data-option-name]');
    if (!picker) return;
    if (variantRecentlyTracked()) return;
    lastVariantTrackedAt = Date.now();

    var optionType =
      input.closest('[data-option-name]')?.getAttribute('data-option-name') ||
      text(picker.querySelector('legend, label')) ||
      input.name;
    var optionValue = input.value;

    track('Product Variant Selected', Object.assign(base(), {
      'product id': ctx.product ? ctx.product.product_id : null,
      'product name': ctx.product ? ctx.product.product_name : null,
      'option type': String(optionType).replace(/:\s*$/, ''),
      'option value': optionValue
    }));
  });

  // Cupra subscribe/one-time plan cards are a purchase-option refinement, not a variant.
  document.addEventListener('click', function (e) {
    var card = e.target.closest && e.target.closest('.cp-plan-card');
    if (!card) return;

    track('Product Variant Selected', Object.assign(base(), {
      'product id': ctx.product ? ctx.product.product_id : null,
      'product name': ctx.product ? ctx.product.product_name : null,
      'variant id': ctx.product ? ctx.product.variant_id : null,
      'option type': 'purchase plan',
      'option value': card.getAttribute('data-plan') || 'unknown'
    }));
  }, true);

  // ── Cart mutation tracking ──
  //
  // Add-to-cart is implemented independently in ~10 bespoke sections (cupra-product,
  // cupra-home, cupra-sets, gift-popup, ...), all of them posting to /cart/*.js.
  // Intercepting at the network layer instruments every one of those call sites,
  // including any added later, instead of editing each section.

  var cartItems = {};
  var cartValue = ctx.cart ? ctx.cart.value : 0;

  // Lines are keyed by what they are, not by Shopify's line key: applying a
  // discount code re-keys every line it touches, which a key-based diff reads
  // as each product being removed and re-added.
  function identity(line) {
    return [line.variant_id, line.selling_plan_id || '', line.is_gift ? 'gift' : ''].join(':');
  }

  function remember(map, line) {
    var id = identity(line);
    if (map[id]) map[id].quantity += line.quantity;
    else map[id] = line;
  }

  (ctx.cart && ctx.cart.items ? ctx.cart.items : []).forEach(function (i) {
    remember(cartItems, Object.assign({}, i));
  });

  function snapshot(cart) {
    var map = {};
    (cart.items || []).forEach(function (line) {
      remember(map, {
        key: line.key,
        product_id: line.product_id,
        variant_id: line.variant_id,
        product_name: line.product_title || (line.product && line.product.title),
        variant_name: line.variant_title,
        quantity: line.quantity,
        price: money(line.final_price),
        selling_plan_id: line.selling_plan_allocation ? String(line.selling_plan_allocation.selling_plan.id) : undefined,
        selling_plan_name: line.selling_plan_allocation && line.selling_plan_allocation.selling_plan.name,
        purchase_type: line.selling_plan_allocation ? 'subscription' : 'one_time',
        is_gift: !!(line.properties && line.properties._is_free_gift)
      });
    });
    return map;
  }

  /** Where the add originated, used for the plan's `add source` property. */
  function addSource() {
    var type = ctx.pageType;
    if (document.querySelector('cart-drawer.active, .cart-drawer.active')) return 'cart drawer';
    if (type === 'product') return 'product page';
    if (type === 'collection' || type === 'list-collections') return 'collection page';
    if (type === 'index') return 'homepage';
    if (type === 'cart') return 'cart page';
    if (type === 'page') return 'landing page';
    return type || 'unknown';
  }

  function diffCart(before, after, operation) {
    var keys = {};
    Object.keys(before).forEach(function (k) { keys[k] = true; });
    Object.keys(after).forEach(function (k) { keys[k] = true; });

    var newValue = 0;
    Object.keys(after).forEach(function (k) {
      newValue += after[k].price * after[k].quantity;
    });
    newValue = Math.round(newValue * 100) / 100;

    Object.keys(keys).forEach(function (key) {
      var was = before[key];
      var now = after[key];
      var wasQty = was ? was.quantity : 0;
      var nowQty = now ? now.quantity : 0;
      var delta = nowQty - wasQty;
      if (delta === 0) return;

      var line = now || was;
      var props = Object.assign(base(), {
        'product id': line.product_id,
        'product name': line.product_name,
        'variant id': line.variant_id,
        'variant name': line.variant_name,
        price: line.price,
        selling_plan_id: line.selling_plan_id,
        selling_plan_name: line.selling_plan_name,
        purchase_type: line.purchase_type,
        quantity: Math.abs(delta),
        'cart value': newValue,
        cart_contents: contents(Object.keys(after).map(function (key) { return after[key]; })),
        product_engagement: engagement({
          product_id: line.product_id,
          product_name: line.product_name,
          variant_id: line.variant_id,
          quantity: Math.abs(delta),
          price: line.price
        })
      });

      if (nowQty === 0) {
        // Line gone entirely — a removal regardless of which endpoint did it.
        track('Product Removed', Object.assign(props, {
          'remove reason': line.is_gift ? 'gift threshold no longer met' : 'user removed'
        }));
      } else if (operation === 'add' && delta > 0) {
        track('Product Added', Object.assign(props, {
          'add source': line.is_gift ? 'free gift' : addSource()
        }));
      } else {
        // Quantity stepper on the cart / drawer.
        track('Cart Updated', Object.assign(props, {
          'update type': delta > 0 ? 'increase' : 'decrease'
        }));
      }
    });

    cartValue = newValue;
  }

  function refreshCart(operation) {
    return fetch(window.routes && window.routes.cart_url ? window.routes.cart_url + '.js' : '/cart.js', {
      headers: { Accept: 'application/json' }
    })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var after = snapshot(cart);
        diffCart(cartItems, after, operation);
        cartItems = after;
      })
      .catch(function (e) {
        console.warn('[amplitude] cart refresh failed:', e);
      });
  }

  var nativeFetch = window.fetch;
  window.fetch = function () {
    var args = arguments;
    var input = args[0];
    var url = typeof input === 'string' ? input : (input && input.url) || '';
    var operation = null;

    if (url.indexOf('/cart/add') !== -1) operation = 'add';
    else if (url.indexOf('/cart/change') !== -1 || url.indexOf('/cart/update') !== -1) operation = 'change';
    else if (url.indexOf('/cart/clear') !== -1) operation = 'change';

    var promise = nativeFetch.apply(window, args);

    if (operation) {
      return promise.then(function (response) {
        // Record the change in the background. Awaiting it here held every
        // add-to-bag and quantity change for an extra /cart.js round trip
        // (up to 1.2 s) before the drawer could open.
        if (response && response.ok) refreshCart(operation);
        return response;
      });
    }

    return promise;
  };

  // Non-AJAX add-to-cart forms navigate away, so diff on the next page load instead.
  document.addEventListener('submit', function (e) {
    var form = e.target;
    if (!form || !form.action || form.action.indexOf('/cart/add') === -1) return;
    if (form.hasAttribute('data-ajax') || form.querySelector('[data-ajax]')) return;

    try {
      sessionStorage.setItem('cupra_pending_add', JSON.stringify({ source: addSource(), at: Date.now() }));
    } catch (err) { /* private mode */ }
  });

  try {
    var pendingAdd = sessionStorage.getItem('cupra_pending_add');
    if (pendingAdd) {
      sessionStorage.removeItem('cupra_pending_add');
      var parsed = JSON.parse(pendingAdd);
      // Only trust it for a moment — a stale entry would mislabel an unrelated pageview.
      if (Date.now() - parsed.at < 30000) refreshCart('add');
    }
  } catch (err) { /* ignore */ }

  // ── Checkout Started ──
  //
  // "Checkout initiated from cart" per the plan. Fires on the theme side; everything
  // after this point happens inside Shopify checkout and belongs to the web pixel.

  document.addEventListener('click', function (e) {
    var button = e.target.closest && e.target.closest('[name="checkout"], a[href*="/checkout"]');
    if (!button) return;

    var items = Object.keys(cartItems).map(function (k) { return cartItems[k]; });
    var quantity = items.reduce(function (sum, i) { return sum + i.quantity; }, 0);

    track('Checkout Button Clicked', Object.assign(base(), {
      'cart value': cartValue,
      quantity: quantity,
      cart_contents: contents(items)
    }));
  }, true);

  // ── Product List Refined ──

  document.addEventListener('change', function (e) {
    var input = e.target;
    if (!input) return;

    var form = input.closest('#FacetFiltersForm, #FacetSortForm, .facets, facet-filters-form');
    if (!form) return;

    var isSort = input.name === 'sort_by';
    track('Product List Refined', Object.assign(base(), {
      'collection name': ctx.collection ? ctx.collection.collection_name : null,
      'refinement type': isSort ? 'sort' : String(input.name || 'filter').replace(/^filter\.[pv]\./, ''),
      'refinement value': input.type === 'checkbox' && !input.checked ? '(cleared) ' + input.value : input.value
    }));
  });

  // ── Article Viewed / Article Completed ──

  if (ctx.pageType === 'article' && ctx.article) {
    var a = ctx.article;
    var articleProps = {
      'article id': a.article_id,
      'article title': a.article_title,
      'article category': a.article_category,
      'publish date': a.publish_date
    };

    track('Article Viewed', Object.assign(base(), articleProps, { 'referrer type': referrerType() }));

    var COMPLETION_THRESHOLD = 90;
    var openedAt = Date.now();
    var completed = false;

    var onScroll = function () {
      if (completed) return;
      var doc = document.documentElement;
      var scrollable = doc.scrollHeight - window.innerHeight;
      if (scrollable <= 0) return;

      var percent = ((window.scrollY || doc.scrollTop) / scrollable) * 100;
      if (percent < COMPLETION_THRESHOLD) return;

      completed = true;
      window.removeEventListener('scroll', onScroll);
      track('Article Completed', Object.assign(base(), articleProps, {
        'completion threshold': COMPLETION_THRESHOLD,
        'time on article seconds': Math.round((Date.now() - openedAt) / 1000)
      }));
    };

    window.addEventListener('scroll', onScroll, { passive: true });

    // ── Article Product Clicked ──
    //
    // Any product link on an article page. The cards from
    // snippets/journal-product-offer.liquid say which one earned the click in
    // `placement` (inline, end, end-alt, sticky, or an *-image variant); a
    // plain link in the article text reports `body-link`. Pair with Article
    // Viewed for an article → product click-through rate. product_handle keeps
    // the name this event was first shipped with (#403).
    document.addEventListener('click', function (e) {
      var link = e.target && e.target.closest ? e.target.closest('a[data-journal-product], a[href*="/products/"]') : null;
      if (!link) return;
      var handle = link.getAttribute('data-journal-product') ||
        ((link.getAttribute('href') || '').split('/products/')[1] || '').split(/[?#\/]/)[0];
      track('Article Product Clicked', Object.assign(base(), articleProps, {
        product_handle: handle,
        'placement': link.getAttribute('data-journal-placement') || 'body-link',
        'scroll depth percent': (function () {
          var doc = document.documentElement;
          var scrollable = doc.scrollHeight - window.innerHeight;
          return scrollable > 0 ? Math.round(((window.scrollY || doc.scrollTop) / scrollable) * 100) : 100;
        })()
      }));
    });
  }

  // ── FAQ Item Opened ──
  //
  // FAQ accordions are native <details> across page-faq, cupra-product and the
  // collection templates, so one delegated listener covers all of them.

  document.addEventListener(
    'toggle',
    function (e) {
      var details = e.target;
      if (!details || details.tagName !== 'DETAILS' || !details.open) return;

      var summary = details.querySelector('summary');
      if (!summary) return;

      // Skip non-FAQ disclosure widgets (menus, filter groups, cart accordions).
      var isFaq =
        details.closest('.faq-item, .cp-faq, [id*="faq" i], [class*="faq" i]') ||
        details.classList.contains('faq-item');
      if (!isFaq) return;

      var question = text(summary).replace(/\s*[+\-−×]\s*$/, '');
      var sectionEl = details.closest('section, .shopify-section, [data-faq-section]');
      var faqSection =
        (sectionEl && sectionEl.getAttribute('data-faq-section')) ||
        text(sectionEl && sectionEl.querySelector('h1, h2')) ||
        ctx.pageType;

      track('FAQ Item Opened', Object.assign(base(), {
        'faq section': faqSection,
        'question id': slug(question),
        'question text': question
      }));
    },
    true // <details> toggle does not bubble
  );

  var formStorage = 'cupra_pending_form';
  var completedForms = {};
  function formEvent(record, status, reason) {
    if (!permitted() || !record || !record.form_attempt_id) return;
    var dedup = record.form_attempt_id + ':' + status;
    if (completedForms[dedup]) return;
    completedForms[dedup] = true;
    var props = Object.assign(base(), record);
    delete props.kind; delete props.at; delete props.path; delete props.status; delete props.reason;
    props.confirmation_source = status === 'success' ? 'shopify_form_response' : undefined;
    if (reason) props.failure_reason = reason;
    if (record.kind === 'review') {
      props.review_id = record.form_attempt_id;
      props.review_source = 'shopify_contact_form';
      props.moderation_status = 'pending';
      track(status === 'success' ? 'Review Submitted' : status === 'error' ? 'Review Submission Failed' : 'Review Submission Started', props);
    } else if (record.kind === 'support') {
      props.ticket_id = record.form_attempt_id;
      props.ticket_source = 'shopify_contact_form';
      track(status === 'success' ? 'Support Ticket Created' : status === 'error' ? 'Support Request Failed' : 'Support Request Started', props);
    } else {
      track('Email Signup ' + (status === 'success' ? 'Completed' : status === 'error' ? 'Failed' : 'Started'), props);
    }
  }
  document.addEventListener('koppa:offer-event', function (event) {
    var detail = event.detail || {};
    if (['Popup Opened', 'Popup Closed', 'Scratch Started', 'Scratch Completed', 'Scratch Skipped', 'Discount Code Revealed'].indexOf(detail.name) === -1) return;
    var p = detail.properties || {};
    track(detail.name, Object.assign(base(), {
      offer_id: p.offer_id, step: p.step, trigger: p.trigger,
      input_method: p.input_method, dismissal_method: p.dismissal_method
    }));
  });

  document.addEventListener('koppa:form-result', function (event) {
    var detail = event.detail || {};
    if (detail.form_id !== 'fyr-email-form' && detail.form_id !== 'csp-subscribe-form') return;
    formEvent(detail, detail.status, detail.reason);
  });
  document.addEventListener('submit', function (event) {
    var form = event.target;
    if (!permitted() || !form || !form.action || form.hasAttribute('data-koppa-ajax')) return;
    if (form.action.indexOf('/contact') === -1) return;
    var type = form.querySelector('[name="form_type"]');
    var newsletter = (type && type.value === 'customer') || !!form.querySelector('[name="contact[tags]"], [name="customer[tags]"]');
    var review = form.id === 'KoppaReviewForm';
    if (!newsletter && !review && form.id !== 'ContactForm') return;
    var section = form.closest('section, .shopify-section');
    var record = {kind:review ? 'review' : newsletter ? 'newsletter' : 'support', form_id:form.id,
      form_attempt_id:crypto.randomUUID(), at:Date.now(), path:location.pathname};
    if (review) {
      var rating = Number((form.querySelector('[name="contact[rating]"]:checked') || {}).value);
      if (!Number.isInteger(rating) || rating < 1 || rating > 5 || !ctx.product) return;
      record.rating = rating;
      record['product id'] = ctx.product.product_id;
      record['product name'] = ctx.product.product_name;
    } else if (newsletter) record['signup location'] = form.getAttribute('data-signup-location') || (section && section.id) || ctx.pageType;
    else {
      record['support channel'] = 'contact form';
      record['support topic'] = (form.querySelector('[name="contact[topic]"],select') || {}).value || 'general';
      record['order id provided'] = !!(form.querySelector('[name*="order"]') || {}).value;
    }
    formEvent(record, 'submitted');
    try { sessionStorage.setItem(formStorage, JSON.stringify(record)); } catch (_) {}
  }, true);
  try {
    var pendingForm = JSON.parse(sessionStorage.getItem(formStorage) || 'null');
    if (pendingForm) {
      var form = document.getElementById(pendingForm.form_id);
      var success = form && form.querySelector('[data-koppa-form-status="success"]');
      var failed = form && form.querySelector('[data-koppa-form-status="error"]');
      var recent = pendingForm.at && Date.now() - pendingForm.at < 600000;
      var samePage = pendingForm.path === location.pathname;
      var customerSuccess = pendingForm.kind === 'newsletter' && new URLSearchParams(location.search).get('customer_posted') === 'true';
      if (!recent || (samePage && (success || failed || customerSuccess))) {
        sessionStorage.removeItem(formStorage);
        if (recent && samePage) formEvent(pendingForm, (success || customerSuccess) ? 'success' : 'error', failed ? 'shopify_validation' : undefined);
      }
    }
  } catch (_) {}

  // ── Region And Currency Changed ──
  //
  // The localization form submits and reloads, so the change is detected by
  // comparing the rendered locale against the previous page's.

  try {
    var LOCALE_KEY = 'cupra_locale';
    var previous = JSON.parse(sessionStorage.getItem(LOCALE_KEY) || 'null');
    var current = { region: ctx.region, currency: ctx.currency, regionName: ctx.regionName };

    if (previous && (previous.region !== current.region || previous.currency !== current.currency)) {
      track('Region And Currency Changed', {
        'previous region': previous.region,
        'new region': current.region,
        'previous currency': previous.currency,
        'new currency': current.currency,
        region: current.region,
        currency: current.currency
      });
    }

    sessionStorage.setItem(LOCALE_KEY, JSON.stringify(current));
  } catch (err) { /* private mode */ }

  // ── Error Encountered ──

  function trackError(category, context, message) {
    if (!message) return;
    track('Error Encountered', Object.assign(base(), {
      'error category': category,
      'error context': context,
      'error message': String(message).slice(0, 500)
    }));
  }

  if (typeof window.subscribe === 'function') {
    try {
      window.subscribe('cart-error', function (event) {
        trackError('cart', 'add to cart', event && (event.message || (event.errors && event.errors.toString())));
      });
    } catch (e) { /* pubsub unavailable */ }
  }

  // Inline cart/form errors rendered by the theme after a failed request.
  var errorObserver = new MutationObserver(function (mutations) {
    mutations.forEach(function (mutation) {
      Array.prototype.forEach.call(mutation.addedNodes, function (node) {
        if (node.nodeType !== 1) return;
        var errorEl = node.matches && node.matches('.form__message--error, .product-form__error-message, .cart__error')
          ? node
          : node.querySelector && node.querySelector('.form__message--error, .product-form__error-message, .cart__error');
        if (!errorEl) return;

        var message = text(errorEl);
        if (message) trackError('form', ctx.pageType, message);
      });
    });
  });

  if (document.body) {
    errorObserver.observe(document.body, { childList: true, subtree: true });
  } else {
    document.addEventListener('DOMContentLoaded', function () {
      errorObserver.observe(document.body, { childList: true, subtree: true });
    });
  }

  document.addEventListener('koppa:cart-result', function (event) {
    var detail = event.detail || {};
    if (detail.kind !== 'discount') return;
    var names = {attempted:'Discount Application Attempted',applied:'Discount Applied',saved:'Discount Saved',pending:'Discount Pending',ineligible:'Discount Rejected',subscription:'Discount Rejected',better:'Existing Discount Kept',removed:'Discount Removed',error:'Discount Application Failed'};
    if (!names[detail.status]) return;
    track(names[detail.status], Object.assign(base(), {source:detail.source, result:detail.status, savings:detail.savings, currency:detail.currency}));
    if (detail.status === 'error') trackError('cart', 'discount application', 'Discount application could not be confirmed');
  });

  // Expose for the sections that want to record something bespoke.
  window.CupraTrack = track;
  }
})();
